import { z } from 'zod';

const requiredError = 'Campo obrigatório';
const emptySectionsError = 'Preencha ao menos uma seção completa: Margem ou Competitividade.';
const marginRangeError = 'O valor deve estar entre -99,9% e 99,9%.';
const competitivenessRangeError = 'O valor deve estar entre 0,1% e 999,9%.';
const minimumGreaterThanMaximumError = 'O % mínimo não pode ser maior que o % máximo.';
const emptyMinimumError = 'Preencha o % mínimo.';
const emptyMaximumError = 'Preencha o % máximo.';
const zeroMinimumError = 'O % mínimo não pode ser 0.';
const zeroMaximumError = 'O % máximo não pode ser 0.';
const objectiveVariationNegativeError = 'A variação não pode ser negativa.';

const MARGIN_MIN = -0.999;
const MARGIN_MAX = 0.999;
const CPI_MIN = 0.001;
const CPI_MAX = 9.999;

const parsePercentInput = (value: string | null) => {
  if (value === null || value.trim() === '') return null;

  const normalized = value.includes(',') ? value.replace(/\./g, '').replace(',', '.') : value;
  const percentage = Number(normalized);

  return Number.isFinite(percentage) ? Number((percentage / 100).toFixed(3)) : Number.NaN;
};

const parseVariationInput = (value: string | null) => {
  if (value === null || value.trim() === '') return null;

  const normalized = value.replace(/\./g, '').replace(',', '.');
  const variation = Number(normalized);

  return Number.isFinite(variation) ? Number(variation.toFixed(1)) : Number.NaN;
};

const percentageField = z.string().nullable().transform(parsePercentInput);
const variationField = z.string().nullable().default(null).transform(parseVariationInput);

export const registerLimitSchema = z
  .object({
    name: z
      .string({ required_error: requiredError })
      .min(2, 'O nome precisa ter ao menos 2 caracteres.')
      .max(255, 'O nome não pode ter mais de 255 caracteres.'),
    marginType: z.enum(['margin', 'objectiveMargin']).default('margin'),
    adjustPricesAccordingToLimits: z.boolean().default(false),
    minMargin: percentageField,
    maxMargin: percentageField,
    downwardVariation: variationField,
    upwardVariation: variationField,
    minCpi: percentageField,
    maxCpi: percentageField,
  })
  .superRefine(({ marginType, minMargin, maxMargin, downwardVariation, upwardVariation, minCpi, maxCpi }, ctx) => {
    const marginIsEmpty = minMargin === null && maxMargin === null;
    const objectiveMarginIsEmpty = downwardVariation === null && upwardVariation === null;
    const marginSectionIsEmpty = marginType === 'objectiveMargin' ? objectiveMarginIsEmpty : marginIsEmpty;
    const competitivenessIsEmpty = minCpi === null && maxCpi === null;

    if (marginSectionIsEmpty && competitivenessIsEmpty) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [marginType === 'objectiveMargin' ? 'downwardVariation' : 'minMargin'],
        message: marginType === 'objectiveMargin' ? 'Preencha ao menos uma seção completa: Margem, Margem objetiva ou Competitividade.' : emptySectionsError,
      });
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['minCpi'], message: emptySectionsError });
    }

    if (marginType === 'margin' && !marginIsEmpty) {
      if (minMargin === null) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['minMargin'], message: emptyMinimumError });
      if (maxMargin === null) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['maxMargin'], message: emptyMaximumError });
    }

    if (marginType === 'objectiveMargin' && !objectiveMarginIsEmpty) {
      const validateObjectiveVariation = (value: number | null, path: 'downwardVariation' | 'upwardVariation') => {
        if (value === null) return;
        if (!Number.isFinite(value) || value < 0) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message: objectiveVariationNegativeError });
        }
      };

      validateObjectiveVariation(downwardVariation, 'downwardVariation');
      validateObjectiveVariation(upwardVariation, 'upwardVariation');
    }

    if (!competitivenessIsEmpty) {
      if (minCpi === null) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['minCpi'], message: emptyMinimumError });
      if (maxCpi === null) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['maxCpi'], message: emptyMaximumError });
    }

    const validateMargin = (value: number | null, path: 'minMargin' | 'maxMargin') => {
      if (value === null) return;
      if (!Number.isFinite(value) || value < MARGIN_MIN || value > MARGIN_MAX) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message: marginRangeError });
      }
    };

    const validateCompetitiveness = (value: number | null, path: 'minCpi' | 'maxCpi') => {
      if (value === null) return;
      if (value === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message: path === 'minCpi' ? zeroMinimumError : zeroMaximumError });
        return;
      }
      if (!Number.isFinite(value) || value < CPI_MIN || value > CPI_MAX) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message: competitivenessRangeError });
      }
    };

    validateMargin(minMargin, 'minMargin');
    validateMargin(maxMargin, 'maxMargin');
    if (maxMargin === 0) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['maxMargin'], message: zeroMaximumError });
    validateCompetitiveness(minCpi, 'minCpi');
    validateCompetitiveness(maxCpi, 'maxCpi');

    if (minMargin !== null && maxMargin !== null && minMargin > maxMargin) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['minMargin'], message: minimumGreaterThanMaximumError });
    }
    if (minCpi !== null && maxCpi !== null && minCpi > maxCpi) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['minCpi'], message: minimumGreaterThanMaximumError });
    }
  });

export type RegisterLimitFormValues = z.input<typeof registerLimitSchema>;
export type RegisterLimitSchemaProps = z.output<typeof registerLimitSchema>;
